"use client";

import { Button } from "@/components/ui/button";
import { UploadSession, useUpload } from "@/providers/UploadProvider";
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  FileImage,
  Loader2,
} from "lucide-react";
import { useCallback } from "react";

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function SessionCard({ session }: { session: UploadSession }) {
  const failedCount = session.files.filter((f) => f.status === "failed").length;

  return (
    <div className="border-b border-gray-100 last:border-0">
      {/* File List - Show for active uploads or if there are errors */}
      {(session.status === "uploading" || failedCount > 0) && (
        <div className="px-3 py-3 space-y-2 max-h-40 overflow-y-auto">
          {session.files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-2 text-xs text-gray-600"
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                {file.status === "completed" ? (
                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                ) : file.status === "failed" ? (
                  <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                ) : file.status === "pending" ? (
                  <FileImage className="h-3.5 w-3.5 text-gray-400" />
                ) : (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate flex-1">{file.file.name}</span>
                  <span className="text-gray-400 flex-shrink-0">
                    {formatFileSize(file.file.size)}
                  </span>
                </div>

                {/* Per-file progress (byte-level during upload) */}
                {session.status === "uploading" && file.status !== "pending" && (
                  <div className="mt-1 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-150 ${
                          file.status === "failed"
                            ? "bg-red-500"
                            : file.status === "completed"
                            ? "bg-green-500"
                            : "bg-blue-600"
                        }`}
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                    <span className="w-10 text-right text-[10px] text-gray-500">
                      {file.status === "completed"
                        ? "100%"
                        : file.status === "failed"
                        ? "—"
                        : `${file.progress}%`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FloatingUploadModal() {
  const {
    sessions,
    isUploading,
    minimized,
    setMinimized,
    clearCompletedSessions,
  } = useUpload();

  const handleClearCompleted = useCallback(() => {
    clearCompletedSessions();
  }, [clearCompletedSessions]);

  // Don't render if no sessions
  if (sessions.length === 0) {
    return null;
  }

  const completedSessions = sessions.filter(
    (s) => s.status === "completed" || s.status === "partial"
  );
  const activeSessions = sessions.filter((s) => s.status === "uploading");
  const failedSessions = sessions.filter((s) => s.status === "failed");

  // Calculate overall stats
  const totalFiles = sessions.reduce((sum, s) => sum + s.files.length, 0);
  const completedFiles = sessions.reduce(
    (sum, s) => sum + s.files.filter((f) => f.status === "completed").length,
    0
  );
  const failedFiles = sessions.reduce(
    (sum, s) => sum + s.files.filter((f) => f.status === "failed").length,
    0
  );
  const overallProgress =
    totalFiles > 0 ? Math.round((completedFiles / totalFiles) * 100) : 0;

  const hasAnyFailures =
    failedFiles > 0 || sessions.some((s) => s.status === "partial");

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer"
        onClick={() => setMinimized(!minimized)}
      >
        <div className="flex items-center gap-2">
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          ) : completedSessions.length > 0 ? (
            <CheckCircle className="h-4 w-4 text-green-500" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-500" />
          )}
          <span className="font-medium text-sm text-gray-900">
            {isUploading
              ? `Uploading ${completedFiles}/${totalFiles}`
              : hasAnyFailures
              ? `Uploads finished with issues (${failedFiles} failed)`
              : "Uploads complete"}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {!isUploading && completedSessions.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleClearCompleted();
              }}
              className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            {minimized ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Overall Progress Bar (always visible) */}
      {isUploading && (
        <div className="h-1 bg-gray-200">
          <div
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
      )}

      {/* Content */}
      {!minimized && (
        <div className="max-h-96 overflow-y-auto">
          {activeSessions.map((session) => (
            <SessionCard key={session.id} session={session} />
          ))}
          {failedSessions.map((session) => (
            <SessionCard key={session.id} session={session} />
          ))}
          {completedSessions.map((session) => (
            <SessionCard key={session.id} session={session} />
          ))}
        </div>
      )}
    </div>
  );
}
